import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { FC } from "react";
import http from "src/utils/httpHelper";
import { useSnackbar } from "notistack";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import i18n from "src/i18n/i18n";

interface ChangePasswordDialogProps {
    isOpen: boolean,
    toggleChangePasswordForm: any
}

const validationSchema = Yup.object().shape({
    current_password: Yup.string()
        .max(255)
        .required(i18n.t("VALIDATION.REQUIRED")),
    password: Yup.string()
        .max(255)
        .required(i18n.t("VALIDATION.REQUIRED")),
    password_confirmation: Yup.string()
        .max(255)
        .required(i18n.t("VALIDATION.REQUIRED")),
});

const ChangePasswordDialog: FC<ChangePasswordDialogProps> = ({ isOpen, toggleChangePasswordForm }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { handleSubmit, formState: { errors, isSubmitting, isValid }, control, reset } = useForm({
        mode: 'onTouched',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            current_password: "",
            password: "",
            password_confirmation: "",
        }
    });

    return (
        <>
            <Dialog
                fullWidth
                maxWidth="sm"
                open={isOpen}
                onClose={() => { toggleChangePasswordForm(); reset(); }}
            >
                <DialogTitle
                    sx={{
                        p: 3,
                    }}
                >
                    <Typography variant="h4" gutterBottom>
                        {t("MY_ACCOUNT.CHANGE_PASSWORD_HEADING")}
                    </Typography>
                    <Typography variant="subtitle2">
                        {t("MY_ACCOUNT.CHANGE_PASSWORD_SUBHEADING")}
                    </Typography>
                </DialogTitle>
                <form onSubmit={handleSubmit(async (values) => {
                    await http.put('/user/password', values)
                        .then((response) => {
                            enqueueSnackbar(t('MY_ACCOUNT.PASSWORD_UPDATED'), { variant: 'success' });
                            reset();
                            toggleChangePasswordForm();
                        })
                        .catch((error: any) => {
                            enqueueSnackbar(error.message, { variant: 'error' });
                        });
                })}>
                    <DialogContent
                        dividers
                        sx={{
                            p: 3,
                        }}
                    >
                        <Grid container spacing={3}>
                            <Grid item xs={12} lg={12}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} lg={12}>
                                        <Controller
                                            name="current_password"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    error={Boolean(errors.current_password)}
                                                    fullWidth
                                                    helperText={errors.current_password?.message}
                                                    label={t("FIELDS.CURRENT_PASSWORD")}
                                                    variant="outlined"
                                                    type={'password'}
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} lg={12}>
                                        <Controller
                                            name="password"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    error={Boolean(errors.password)}
                                                    fullWidth
                                                    helperText={errors.password?.message}
                                                    label={t("FIELDS.PASSWORD")}
                                                    variant="outlined"
                                                    type={'password'}
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} lg={12}>
                                        <Controller
                                            name="password_confirmation"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    error={Boolean(errors.password_confirmation)}
                                                    fullWidth
                                                    helperText={errors.password_confirmation?.message}
                                                    label={t("FIELDS.PASSWORD_CONFIRMATION")}
                                                    variant="outlined"
                                                    type={'password'}
                                                    {...field}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            p: 3,
                        }}
                    >
                        <Button color="secondary" onClick={toggleChangePasswordForm}>
                            {t("Cancel")}
                        </Button>
                        <Button
                            type="submit"
                            startIcon={
                                isSubmitting ? <CircularProgress size="1rem" /> : null
                            }
                            disabled={isSubmitting || !isValid}
                            variant="contained"
                        >
                            {t("MY_ACCOUNT.BTN_CHANGE_PASSWORD")}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    )
}
export default ChangePasswordDialog;